import React, { Component } from 'react';
import {
  any,
  array,
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import { Helmet } from 'react-helmet-async';
import loadable from '@loadable/component';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl, intlShape } from '../../util/reactIntl';
import { metaTagProps } from '../../util/seo';
import { buildAlternateLinks, canonicalRoutePath } from '../../util/routes';
import { propTypes } from '../../util/types';
import { apiBaseUrl } from '../../util/api';
import { TRANSLATED_PATHS } from '../../util/urlHelpers';

import css from './Page.module.css';

const FloatingCheckoutWidget = loadable(() =>
  import(/* webpackChunkName: "FloatingCheckoutWidget" */ '../FloatingCheckoutWidget/FloatingCheckoutWidget')
);

/**
 * Get all localized path segments for given route names.
 * This extracts the first path segment from each localized route path.
 * e.g., ListingPage: { en: '/rentals/:slug/:id', fr: '/locations/:slug/:id' }
 *       → ['/rentals', '/locations', '/alquileres', '/租赁']
 */
const getAllPathVariations = (routeNames) => {
  const paths = [];
  routeNames.forEach(routeName => {
    const translations = TRANSLATED_PATHS[routeName];
    if (translations) {
      Object.values(translations).forEach(path => {
        // Extract the first path segment (e.g., '/rentals/:slug/:id' → 'rentals')
        const segments = path.split('/').filter(Boolean);
        const firstSegment = segments[0];
        // Skip dynamic segments (starting with ':')
        if (firstSegment && !firstSegment.startsWith(':')) {
          paths.push(`/${firstSegment}`);
        }
      });
    }
  });
  return [...new Set(paths)]; // Return unique paths
};

// Routes that need reactDates.css for date pickers.
const DATE_PICKER_ROUTE_NAMES = [
  'SearchPage',
  'StoreProfilePage',
  'ListingPage',
  'ListingPageVariant',
  'CheckoutPage',
  'CheckoutPageOld',
  'ProfilePage',
  'InboxPage',
  'InboxBasePage',
  'OrderDetailsPage',
  'OrderDetailsPageRedirect',
  'SaleDetailsPage',
  'SaleDetailsPageRedirect',
  'CartPage',
  'AccountSettingsPage',
  'ContactDetailsPage',
  'ManageListingsPage',
  // RentAnythingWizardPage, AIUpdateListingPage, and PublishedListingPage all
  // have full TRANSLATED_PATHS entries (fr/es/zh use fully translated URL
  // segments, e.g. /fr/assistant-location/... for the wizard), so they're
  // covered by getAllPathVariations() like every other route here -
  // previously they were matched via a separate hardcoded English-only path
  // list, which silently missed the date-picker CSS on these pages for
  // fr/es/zh requests.
  'RentAnythingWizardPage',
  'AIUpdateListingPage',
  'PublishedListingPage',
];

export const needsDatePickerCSS = pathname => {
  const datePickerPaths = getAllPathVariations(DATE_PICKER_ROUTE_NAMES);
  return datePickerPaths.some(path => pathname.includes(path));
};

const preventDefault = e => {
  e.preventDefault();
};

const twitterPageURL = siteTwitterHandle => {
  if (siteTwitterHandle && siteTwitterHandle.charAt(0) === '@') {
    return `https://twitter.com/${siteTwitterHandle.substring(1)}`;
  } else if (siteTwitterHandle) {
    return `https://twitter.com/${siteTwitterHandle}`;
  }
  return null;
};

const webmanifestURL = marketplaceRootURL => {
  // Note: on localhost (when running "yarn run dev"), the webmanifest is running on apiServer port
  const baseUrl = apiBaseUrl(marketplaceRootURL);
  return `${baseUrl}/site.webmanifest`;
};

const getFaviconVariants = config => {
  // We add favicon through hosted configs
  // NOTE: There's no favicon.ico file. This is an imageAsset object which is used together with <meta> tags.
  const favicon = config.branding.favicon;
  return favicon?.type === 'imageAsset'
    ? Object.values(favicon.attributes.variants)
    : [];
};

const getAppleTouchIconURL = config => {
  // The appIcon is used to pick apple-touch-icon
  // We use 180x180. I.e. we follow the example set by realfavicongenerator
  const appIcon = config.branding.appIcon;
  const appIconVariants =
    appIcon?.type === 'imageAsset'
      ? Object.values(appIcon.attributes.variants)
      : [];
  const appleTouchIconVariant = appIconVariants.find(variant => {
    return variant.width === 180 && variant.height === 180;
  });
  return appleTouchIconVariant?.url;
};

class PageComponent extends Component {
  constructor(props) {
    super(props);
    // Keeping scrollPosition out of state reduces rendering cycles (and no bad states rendered)
    this.scrollPosition = 0;
    this.contentDiv = null;
    this.scrollingDisabledChanged = this.scrollingDisabledChanged.bind(this);
  }

  componentDidMount() {
    // By default a dropped file is loaded in the browser window as a
    // file URL. We want to prevent this since it might loose a lot of
    // data the user has typed but not yet saved. Preventing requires
    // handling both dragover and drop events.
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    // Remove duplicated server-side rendered page schema.
    // It's in <body> to improve initial rendering performance,
    // but after web app is initialized, react-helmet-async operates with <head>
    const pageSchema = document.getElementById('page-schema');
    if (pageSchema) {
      pageSchema.remove();
    }

    // Conditionally load CSS based on current route
    this.loadConditionalCSS();
  }

  componentWillUnmount() {
    document.removeEventListener('dragover', preventDefault);
    document.removeEventListener('drop', preventDefault);
  }

  scrollingDisabledChanged(currentScrollingDisabled) {
    if (
      currentScrollingDisabled &&
      currentScrollingDisabled !== this.scrollingDisabled
    ) {
      // Update current scroll position, if scrolling is disabled (e.g. modal is open)
      this.scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      this.scrollingDisabled = currentScrollingDisabled;
    } else if (currentScrollingDisabled !== this.scrollingDisabled) {
      this.scrollingDisabled = currentScrollingDisabled;
    }
  }

  loadConditionalCSS = () => {
    const { location } = this.props;
    const pathname = location.pathname;

    if (needsDatePickerCSS(pathname)) {
      this.loadCSS('/static/reactDates.css', 'reactDates');
    }

    // Mapbox CSS is now included via IncludeScripts (Helmet) so it reaches the
    // SSR <head> and starts loading before any map component mounts, eliminating
    // the "missing CSS declarations" race condition.

    this.loadCSS('https://cdn.jsdelivr.net/npm/remixicon@4.6.0/fonts/remixicon.css', 'remixicon');
  }

  componentDidUpdate(prevProps) {
    // Re-evaluate CSS needs when route changes
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.loadConditionalCSS();
    }
  }

  loadCSS = (href, id) => {
    // Check if already loaded
    if (document.querySelector(`link[data-css-id="${id}"]`)) {
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-css-id', id);
    document.head.appendChild(link);
  }

  render() {
    const {
      className,
      rootClassName,
      children,
      location,
      intl,
      scrollingDisabled,
      referrer,
      author,
      openGraphType,
      description,
      facebookImages,
      published,
      schema,
      socialSharing,
      title,
      twitterHandle,
      twitterImages,
      updated,
      config,
      routeConfiguration,
      currentListing,
      pageAssetsData,
      canonicalUrl: canonicalUrlProp,
      robots,
      currentUser,
      imageAlt: imageAltProp,
      preloadImage,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className, {
      [css.scrollingDisabled]: scrollingDisabled,
    });

    this.scrollingDisabledChanged(scrollingDisabled);

    const marketplaceRootURL = config.marketplaceRootURL;
    const shouldReturnPathOnly = referrer && referrer !== 'unsafe-url';
    const canonicalPath = canonicalRoutePath(
      routeConfiguration,
      location,
      true
    );
    const canonicalUrl = encodeURI(canonicalUrlProp || `${marketplaceRootURL}${canonicalPath}`);

    const alternateLinks = buildAlternateLinks({
      config,
      routes: routeConfiguration,
      location,
      listing: currentListing,
      pageAssetsData,
    });

    const marketplaceName = config.marketplaceName;
    const schemaTitle = intl.formatMessage(
      { id: 'Page.schemaTitle' },
      { marketplaceName }
    );
    const schemaDescription = intl.formatMessage({
      id: 'Page.schemaDescription',
    });
    const pageTitle = title || schemaTitle;
    const pageDescription = description || schemaDescription;
    const {
      title: socialSharingTitle,
      description: socialSharingDescription,
      images1200: socialSharingImages1200,
      // Note: we use image with open graph's aspect ratio (1.91:1) also with Twitter
      images600: socialSharingImages600,
    } = socialSharing || {};

    // Images for social media sharing
    const defaultFacebookImageURL = config.branding.facebookImage;
    const openGraphFallbackImages = [
      {
        name: 'facebook',
        url: defaultFacebookImageURL,
        width: 1200,
        height: 630,
      },
    ];
    const defaultTwitterImageURL = config.branding.twitterImage;
    const twitterFallbackImages = [
      {
        name: 'twitter',
        url: defaultTwitterImageURL,
        width: 600,
        height: 314,
      },
    ];
    const facebookImgs =
      socialSharingImages1200 || facebookImages || openGraphFallbackImages;
    const twitterImgs =
      socialSharingImages600 || twitterImages || twitterFallbackImages;

    const currentStoreName = currentUser?.attributes?.profile?.publicData?.storeName
    const isStoreOwner = author && typeof author === 'string' ? currentStoreName === author : true;
    const storeCity = currentUser?.attributes?.profile?.publicData?.city;
    
    const imageAlt = imageAltProp
      || (currentListing
        ? currentListing?.attributes?.publicData?.listingImages?.[0]?.seoTags
            ?.altText ||
          `${currentListing?.attributes?.title} for rent in ${location}`
        : currentUser && isStoreOwner
        ? currentUser.attributes.profile.publicData.seoTitle ||
          `${currentStoreName} is now offering quality rentals in the ${storeCity} on Rent Anything Store.`
        : `${currentStoreName} is now offering quality rentals on Rent Anything Store.`);

    const metaToHead = metaTagProps(
      {
        author,
        openGraphType,
        socialSharingTitle: socialSharingTitle || pageTitle,
        socialSharingDescription: socialSharingDescription || pageDescription,
        description: pageDescription,
        facebookImages: facebookImgs,
        twitterImages: twitterImgs,
        twitterHandle,
        published,
        updated,
        url: canonicalUrl,
        locale: intl.locale,
        imageAlt
      },
      config
    );

    const facebookPage = config.siteFacebookPage;
    const twitterPage = twitterPageURL(config.siteTwitterHandle);
    const instagramPage = config.siteInstagramPage;
    const sameOrganizationAs = [
      facebookPage,
      twitterPage,
      instagramPage,
    ].filter(v => v != null);

    // Schema for search engines (helps them to understand what this page is about)
    // http://schema.org
    // We are using JSON-LD format

    // Schema attribute can be either single schema object or an array of objects
    // This makes it possible to include several different items from the same page.
    // E.g. Product, Place, Video
    const hasSchema = schema != null;
    const schemaFromProps =
      hasSchema && Array.isArray(schema) ? schema : hasSchema ? [schema] : [];
    const normalizedSchemaFromProps = schemaFromProps
      .map(schemaItem => {
        if (!schemaItem || typeof schemaItem !== 'object' || Array.isArray(schemaItem)) {
          return null;
        }

        // Ensure external parsers (e.g. GTM tags) don't crash on a missing or
        // non-string @context — they often call `@context.toLowerCase()` without
        // guarding. Always emit a canonical string so third-party validators are safe.
        const existingContext = schemaItem['@context'];
        const context =
          typeof existingContext === 'string' && existingContext.trim()
            ? existingContext
            : 'https://schema.org';

        return { ...schemaItem, '@context': context };
      })
      .filter(Boolean);
    const addressMaybe = config.address?.streetAddress
      ? { address: config.address }
      : {};
    const schemaArrayJSONString = JSON.stringify([
      ...normalizedSchemaFromProps,
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${marketplaceRootURL}#organization`,
        url: marketplaceRootURL,
        name: marketplaceName,
        sameAs: sameOrganizationAs,
        logo: config.branding.logoImageMobileURL,
        ...addressMaybe,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        url: marketplaceRootURL,
        description: schemaDescription,
        name: schemaTitle,
      },
    ]);

    const scrollPositionStyles = scrollingDisabled
      ? { marginTop: `${-1 * this.scrollPosition}px` }
      : {};

    // If scrolling is not disabled, but content element has still scrollPosition set
    // in style attribute, we scrollTo scrollPosition.
    const hasMarginTopStyle =
      this.contentDiv && this.contentDiv.style.marginTop;
    if (!scrollingDisabled && hasMarginTopStyle) {
      window.requestAnimationFrame(() => {
        window.scrollTo(0, this.scrollPosition);
      });
    }

    const faviconVariants = getFaviconVariants(config);
    const appleTouchIcon = getAppleTouchIconURL(config);

    // Marketplace color and branding image comes from configs
    // If set, we need to create CSS Property and set it to DOM (documentElement is selected here)
    // Note: this is also set to <html> element in app.js to provide marketplace colors for modals/portals.
    const styles = {
      ['--marketplaceColor']: config.branding.marketplaceColor,
      ['--marketplaceColorDark']: config.branding.marketplaceColorDark,
      ['--marketplaceColorLight']: config.branding.marketplaceColorLight,
    };

    return (
      <div className={classes} style={styles} id="page">
        <Helmet
          htmlAttributes={{
            lang: intl.locale,
          }}
        >
          <title>{pageTitle}</title>
          {referrer ? <meta name="referrer" content={referrer} /> : null}
          <link rel="canonical" href={canonicalUrl} />
          {alternateLinks?.length > 0
            ? alternateLinks.map(link => (
                <link
                  key={link.lang}
                  rel="alternate"
                  hrefLang={link.lang}
                  href={link.href}
                />
              ))
            : null}

          {/* Network hints are managed globally in public/index.html */}

          {/* Page-supplied LCP image hint. Rendered through Helmet (not as a child of Page)
              so it reaches <head> via the server's ssrLinks placeholder and is discoverable
              before the render-blocking stylesheets resolve — a preload emitted mid-<body>
              is only found once the parser reaches it. The href/imagesrcset must be produced
              by buildCloudinaryImageSources so they match the <img> the page goes on to
              render; a mismatched hint costs a full extra download and helps nothing. */}
          {preloadImage?.href ? (
            <link
              rel="preload"
              as="image"
              href={preloadImage.href}
              {...(preloadImage.imagesrcset ? { imagesrcset: preloadImage.imagesrcset } : {})}
              {...(preloadImage.imagesizes ? { imagesizes: preloadImage.imagesizes } : {})}
              {...(preloadImage.type ? { type: preloadImage.type } : {})}
              fetchpriority="high"
            />
          ) : null}

          {/* Site-wide logo preloads to improve first paint */}
          {config.branding?.logoImageDesktopURL ? (
            <link rel="preload" as="image" href={config.branding.logoImageDesktopURL} fetchpriority="high" />
          ) : null}
          {config.branding?.logoImageMobileURL ? (
            <link rel="preload" as="image" href={config.branding.logoImageMobileURL} fetchpriority="high" />
          ) : null}
          {faviconVariants.map(variant => {
            return (
              <link
                key={`icon_${variant.width}`}
                rel="icon"
                type="image/png"
                sizes={`${variant.width}x${variant.height}`}
                href={variant.url}
              />
            );
          })}

          {appleTouchIcon ? (
            <link
              rel="apple-touch-icon"
              sizes="180x180"
              href={appleTouchIcon}
            />
          ) : null}

          <link rel="manifest" href={webmanifestURL(marketplaceRootURL)} />

          <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
          <meta httpEquiv="Content-Language" content={intl.locale} />
          {metaToHead.map((metaProps, i) => (
            <meta key={i} {...metaProps} />
          ))}
          <script id="page-schema" type="application/ld+json">
            {schemaArrayJSONString.replace(/</g, '\\u003c')}
          </script>
          {robots ? <meta name="robots" content={robots} /> : null}
        </Helmet>

        <div
          className={css.content}
          style={scrollPositionStyles}
          ref={c => {
            this.contentDiv = c;
          }}
        >
          {children}
        </div>

        {/* Floating Checkout Widget - Self-contained */}
        <FloatingCheckoutWidget
          intl={intl}
          currentUserCurrency={this.props.currentUserCurrency}
          exchangeRates={this.props.exchangeRates}
          currentUser={this.props.currentUser}
          dispatch={this.props.dispatch}
          history={this.props.history}
          routeConfiguration={routeConfiguration}
          lineItems={this.props.lineItems}
          fetchLineItemsInProgress={this.props.fetchLineItemsInProgress}
        />
      </div>
    );
  }
}

PageComponent.defaultProps = {
  className: null,
  rootClassName: null,
  children: null,
  author: null,
  openGraphType: 'website',
  description: null,
  facebookImages: null,
  twitterImages: null,
  published: null,
  referrer: null,
  schema: null,
  socialSharing: null,
  twitterHandle: null,
  updated: null,
  canonicalUrl: null,
  robots: null,
  imageAlt: null,
  preloadImage: null,
};

PageComponent.propTypes = {
  className: string,
  rootClassName: string,
  children: any,
  scrollingDisabled: bool.isRequired,

  // Handle referrer policy
  referrer: string,

  // SEO related props
  author: string,
  openGraphType: string, // og:type
  description: string, // page description
  facebookImages: arrayOf(
    shape({
      width: number.isRequired,
      height: number.isRequired,
      url: string.isRequired,
    })
  ),
  twitterImages: arrayOf(
    shape({
      width: number.isRequired,
      height: number.isRequired,
      url: string.isRequired,
    })
  ),
  published: string, // article:published_time
  schema: oneOfType([object, array]), // http://schema.org
  socialSharing: shape({
    title: string,
    description: string,
    images1200: arrayOf(
      // Page asset file can define this
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
    images600: arrayOf(
      // Page asset file can define this
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
  }),
  title: string, // page title
  twitterHandle: string, // twitter handle
  updated: string, // article:modified_time
  canonicalUrl: string,
  robots: string,
  imageAlt: string,
  // { href, imagesrcset?, imagesizes?, type? } for this page's LCP image
  preloadImage: shape({
    href: string.isRequired,
    imagesrcset: string,
    imagesizes: string,
    type: string,
  }),

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useLocation
  location: shape({
    pathname: string.isRequired,
    search: string.isRequired,
    hash: string.isRequired,
  }).isRequired,
};

const Page = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const location = useLocation();
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();

  // Get current user and exchange rates for currency conversion
  const currentUser = useSelector(state => state.user.currentUser);
  const exchangeRates = useSelector(state => state.exchangeRate.exchangeRates);
  const guestUserCurrency = useSelector(state => state.user.currentUserCurrency);
  
  // Use logged-in user's currency, or guest user's selected currency, or default config currency
  const currentUserCurrency = currentUser?.attributes?.profile?.publicData?.currency || guestUserCurrency || config.currency;

  // Line items from CartPage reducer (widget will use these)
  const lineItems = useSelector(state => state.CartPage.lineItems);
  const fetchLineItemsInProgress = useSelector(state => state.CartPage.fetchLineItemsInProgress);

  return (
    <PageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      location={location}
      intl={intl}
      currentUserCurrency={currentUserCurrency}
      exchangeRates={exchangeRates}
      currentUser={currentUser}
      dispatch={dispatch}
      history={history}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      {...props}
    />
  );
};

export default Page;
